import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { openErrorNotification } from "../../helpers/methods";
import CallFilterBar, {
  CallFilters,
  callFiltersToParams,
  parseCallFiltersFromSearch,
} from "./CallFilterBar";
import CallingLeadsModal, { CallingLeadRow } from "./CallingLeadsModal";
import {
  exportCallingReport,
  getCallingReport,
  getCallingReportLeads,
} from "./callingReportService";

type Col = { key: string; label: string; group?: string };
type Group = { key: string; label: string; columns: Col[] };
type Row = {
  id: string;
  name: string;
  rowKind?: string;
  groupId?: string;
  date?: string;
  dateLabel?: string;
  assignedLeads: number;
  freshLeads?: number;
  recordings: number;
  buckets: Record<string, number>;
  connected: number;
  notConnected: number;
  totalCalls: number;
  connectPct: number;
};

const ASSIGNED_HINT =
  "Leads given to this salesperson on this date — counted on the assignment / mapping day, even if the lead was created earlier.";
const FRESH_HINT =
  "New leads that arrived this date and are currently assigned to this salesperson.";

const ColumnHint: React.FC<{ text: string }> = ({ text }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (btnRef.current?.contains(target) || tipRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={text}
        className="ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-white/70 bg-[#111827] text-[10px] font-bold leading-none text-white hover:bg-white hover:text-[#111827]"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          const rect = event.currentTarget.getBoundingClientRect();
          setPos({
            top: rect.bottom + 8,
            left: Math.max(12, Math.min(rect.right - 268, window.innerWidth - 292)),
          });
          setOpen((value) => !value);
        }}
      >
        i
      </button>
      {open
        ? createPortal(
            <div
              ref={tipRef}
              role="tooltip"
              style={{
                position: "fixed",
                top: pos.top,
                left: pos.left,
                zIndex: 10050,
                width: 268,
                padding: "10px 12px",
                borderRadius: 10,
                background: "#111827",
                color: "#ffffff",
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.45,
                boxShadow: "0 12px 32px rgba(15,23,42,0.28)",
                textTransform: "none",
                letterSpacing: "normal",
                textAlign: "left",
              }}
            >
              {text}
            </div>,
            document.body
          )
        : null}
    </>
  );
};

const uniqueCols = (cols: Col[] = []) => {
  const seen = new Set<string>();
  return cols.filter((col) => {
    if (!col?.key || seen.has(col.key)) return false;
    seen.add(col.key);
    return true;
  });
};

const sumBuckets = (row: { buckets?: Record<string, number> } | undefined, cols: Col[]) =>
  cols.reduce((sum, col) => sum + (Number(row?.buckets?.[col.key]) || 0), 0);

const callMetrics = (
  row: { buckets?: Record<string, number>; recordings?: number } | undefined,
  connectedCols: Col[],
  notConnectedCols: Col[]
) => {
  const connected = sumBuckets(row, connectedCols);
  const notConnected = sumBuckets(row, notConnectedCols);
  const totalCalls = connected + notConnected;
  return {
    connected,
    notConnected,
    totalCalls,
    connectPct: totalCalls ? Math.round((connected / totalCalls) * 100) : 0,
    recordings: row?.recordings || 0,
  };
};

const CallingReport: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState<any>(null);
  const [popup, setPopup] = useState<{
    open: boolean;
    title: string;
    loading: boolean;
    leads: CallingLeadRow[];
  }>({ open: false, title: "", loading: false, leads: [] });
  const [filters, setFilters] = useState<CallFilters>(() =>
    parseCallFiltersFromSearch(location.search)
  );

  const query = useMemo(() => callFiltersToParams(filters), [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getCallingReport(query);
      if (res?.success === 0) {
        openErrorNotification(res?.message || "Could not load calling report");
        return;
      }
      setData(res);
    } catch (err: any) {
      openErrorNotification(err?.message || "Could not load calling report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query.period,
    query.from,
    query.to,
    query.rowBy,
    filters.teamIds,
    filters.agentIds,
    filters.projectIds,
    filters.sources,
    filters.formIds,
    filters.stages,
  ]);

  const connectedGroups: Group[] = (() => {
    const seen = new Set<string>();
    const raw: Group[] = (
      data?.connectedGroups?.length
        ? data.connectedGroups
        : [
            {
              key: "connected",
              label: "Connected",
              columns: data?.connectedColumns || [],
            },
          ]
    ) as Group[];
    return raw
      .map((group) => ({
        ...group,
        columns: uniqueCols(group.columns).filter((col) => {
          if (seen.has(col.key)) return false;
          seen.add(col.key);
          return true;
        }),
      }))
      .filter((group) => group.columns.length);
  })();
  const connected: Col[] = connectedGroups.flatMap((group) => group.columns || []);
  const notConnected: Col[] = uniqueCols(data?.notConnectedColumns || []);
  const headerRows = 3;
  const rows: Row[] = data?.rows || [];
  const totals = data?.totals;
  const totalMetrics = callMetrics(totals, connected, notConnected);
  const rowBy = filters.rowBy;
  const rowLabel =
    rowBy === "team"
      ? "Team"
      : rowBy === "project"
        ? "Project"
        : rowBy === "source"
          ? "Source"
          : rowBy === "form"
            ? "Form"
            : "Salesperson";

  const openLeadsPopup = async (
    row?: Row,
    opts?: { bucket?: string; assigned?: boolean; fresh?: boolean }
  ) => {
    const agentId =
      rowBy === "salesperson"
        ? row?.groupId || (row?.rowKind === "subtotal" ? row.id : "")
        : "";
    const date = row?.date || query.from || "";
    const stageLabel = opts?.bucket
      ? connected.find((col) => col.key === opts.bucket)?.label ||
        notConnected.find((col) => col.key === opts.bucket)?.label ||
        ""
      : "";
    const kind = opts?.assigned
      ? "assigned"
      : opts?.fresh
        ? "fresh"
        : opts?.bucket
          ? "stage"
          : "all";
    const titleParts = [
      row?.name || "Leads",
      row?.dateLabel || "",
      opts?.assigned
        ? "Assigned"
        : opts?.fresh
          ? "Fresh"
          : stageLabel || "All",
    ].filter(Boolean);
    setPopup({
      open: true,
      title: titleParts.join(" · "),
      loading: true,
      leads: [],
    });
    try {
      const res = await getCallingReportLeads({
        ...query,
        agentId: agentId || undefined,
        date: date || undefined,
        kind,
        bucket: opts?.bucket,
      });
      setPopup((prev) => ({
        ...prev,
        loading: false,
        leads: Array.isArray(res?.data) ? res.data : [],
      }));
    } catch (err: any) {
      setPopup((prev) => ({ ...prev, loading: false, leads: [] }));
      openErrorNotification(err?.message || "Could not load leads");
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await exportCallingReport(query);
    } catch (err: any) {
      openErrorNotification(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const cellClass = (value: number) =>
    value
      ? "cursor-pointer text-[#111827] hover:bg-[#F9F0F4]"
      : "text-[#9CA3AF]";

  const colMinWidth = (label: string, min = 72) =>
    Math.max(min, Math.min(String(label || "").length * 7 + 24, 132));

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white">
      <div className="shrink-0 border-b border-[#EEF0F5] px-4 py-4 sm:px-5 lg:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
              Daily calling report
            </h1>
            <p className="mt-1 text-sm text-[#6B7280]">
              How many calls each salesperson made each day, connect %, assigned
              leads, fresh leads, and recordings
              {data?.period?.label ? ` · ${data.period.label}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onExport}
            disabled={exporting}
            className="inline-flex h-10 items-center rounded-xl bg-[#0F766E] px-4 text-sm font-semibold text-white shadow-sm hover:bg-[#0D9488] disabled:opacity-60"
          >
            {exporting ? "Exporting…" : "Export Excel"}
          </button>
        </div>
        <div className="mt-4">
          <CallFilterBar value={filters} onChange={setFilters} showRowBy />
        </div>
      </div>

      <div className="min-h-0 flex-1 p-4 sm:p-5 lg:p-6">
        <div className="h-full min-h-0 overflow-auto rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
          {loading && !data ? (
            <p className="p-8 text-sm text-[#6B7280]">Loading calling report…</p>
          ) : (
            <table className="w-max border-collapse text-sm">
              <colgroup>
                <col style={{ width: 200 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 168 }} />
                <col style={{ width: 168 }} />
                {connected.map((col) => (
                  <col key={`c-${col.key}`} style={{ width: colMinWidth(col.label) }} />
                ))}
                {notConnected.map((col) => (
                  <col key={`n-${col.key}`} style={{ width: colMinWidth(col.label) }} />
                ))}
                <col style={{ width: 110 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 110 }} />
              </colgroup>
              <thead>
                <tr>
                  <th
                    rowSpan={headerRows}
                    className="sticky left-0 z-30 whitespace-nowrap border-b border-r border-[#E5E7EB] bg-[#111827] px-4 py-3 text-left font-semibold text-white"
                    style={{ minWidth: 200 }}
                  >
                    {rowLabel}
                  </th>
                  <th
                    rowSpan={headerRows}
                    className="whitespace-nowrap border-b border-[#E5E7EB] bg-[#111827] px-4 py-3 text-left font-semibold text-white"
                    style={{ minWidth: 120 }}
                  >
                    Date
                  </th>
                  <th
                    rowSpan={headerRows}
                    className="whitespace-nowrap border-b border-[#E5E7EB] bg-[#111827] px-4 py-3 text-right font-semibold text-white"
                    style={{ minWidth: 168 }}
                  >
                    <span className="inline-flex items-center justify-end">
                      Total Lead Assign
                      <ColumnHint text={ASSIGNED_HINT} />
                    </span>
                  </th>
                  <th
                    rowSpan={headerRows}
                    className="whitespace-nowrap border-b border-[#E5E7EB] bg-[#111827] px-4 py-3 text-right font-semibold text-white"
                    style={{ minWidth: 168 }}
                  >
                    <span className="inline-flex items-center justify-end">
                      Total Fresh Leads
                      <ColumnHint text={FRESH_HINT} />
                    </span>
                  </th>
                  {connected.length ? (
                    <th
                      colSpan={connected.length}
                      className="whitespace-nowrap border-b border-[#A7F3D0] bg-[#D1FAE5] px-4 py-2 text-center font-semibold text-[#065F46]"
                    >
                      Connected
                    </th>
                  ) : null}
                  {notConnected.length ? (
                    <th
                      colSpan={notConnected.length}
                      className="whitespace-nowrap border-b border-[#E5E7EB] bg-[#F3F4F6] px-4 py-2 text-center font-semibold text-[#374151]"
                    >
                      Not Connected
                    </th>
                  ) : null}
                  <th
                    rowSpan={headerRows}
                    className="whitespace-nowrap border-b border-[#E5E7EB] bg-[#111827] px-4 py-3 text-right font-semibold text-white"
                    style={{ minWidth: 110 }}
                  >
                    Total Calls
                  </th>
                  <th
                    rowSpan={headerRows}
                    className="whitespace-nowrap border-b border-[#E5E7EB] bg-[#111827] px-4 py-3 text-right font-semibold text-white"
                    style={{ minWidth: 100 }}
                  >
                    Connect %
                  </th>
                  <th
                    rowSpan={headerRows}
                    className="whitespace-nowrap border-b border-[#E5E7EB] bg-[#111827] px-4 py-3 text-right font-semibold text-white"
                    style={{ minWidth: 110 }}
                  >
                    Recordings
                  </th>
                </tr>
                <tr>
                  {connectedGroups.map((group) => (
                    <th
                      key={group.key}
                      colSpan={Math.max(group.columns?.length || 1, 1)}
                      className="whitespace-nowrap border-b border-[#A7F3D0] bg-[#A7F3D0] px-3 py-2 text-center text-xs font-semibold text-[#065F46]"
                    >
                      {group.label}
                    </th>
                  ))}
                  {notConnected.length ? (
                    <th
                      colSpan={notConnected.length}
                      className="whitespace-nowrap border-b border-[#E5E7EB] bg-[#E5E7EB] px-3 py-2 text-center text-xs font-semibold text-[#374151]"
                    >
                      Call status
                    </th>
                  ) : null}
                </tr>
                <tr>
                  {connected.map((col) => (
                    <th
                      key={`h-${col.key}`}
                      className="border border-[#A7F3D0] bg-[#ECFDF5] px-1 py-2 text-center align-bottom text-xs font-semibold leading-tight text-[#065F46]"
                      style={{ width: colMinWidth(col.label), minWidth: colMinWidth(col.label), maxWidth: 132 }}
                    >
                      <span className="block whitespace-normal break-words">
                        {col.label}
                      </span>
                    </th>
                  ))}
                  {notConnected.map((col) => (
                    <th
                      key={`h-${col.key}`}
                      className="border border-[#E5E7EB] bg-[#F9FAFB] px-1 py-2 text-center align-bottom text-xs font-semibold leading-tight text-[#4B5563]"
                      style={{ width: colMinWidth(col.label), minWidth: colMinWidth(col.label), maxWidth: 132 }}
                    >
                      <span className="block whitespace-normal break-words">
                        {col.label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isSubtotal = row.rowKind === "subtotal";
                  const rowBg = isSubtotal ? "bg-[#F3F4F6]" : "bg-white";
                  const metrics = callMetrics(row, connected, notConnected);
                  return (
                  <tr key={row.id} className={`border-b border-[#F3F4F6] hover:bg-[#FCFAFB] ${rowBg}`}>
                    <td
                      className={`sticky left-0 z-10 whitespace-nowrap border-r border-[#F3F4F6] px-4 py-2.5 font-semibold text-[#111827] ${rowBg}`}
                      style={{ minWidth: 200 }}
                    >
                      {row.name}
                    </td>
                    <td
                      className="cursor-pointer whitespace-nowrap px-4 py-2.5 tabular-nums text-[#4B5563] hover:bg-[#F9F0F4]"
                      onClick={() => openLeadsPopup(row, { assigned: true })}
                    >
                      {row.dateLabel ||
                        row.date ||
                        (isSubtotal
                          ? "Total"
                          : filters.range.from &&
                            filters.range.from === filters.range.to
                            ? filters.range.from
                            : "—")}
                    </td>
                    <td
                      className={`whitespace-nowrap px-4 py-2.5 text-right tabular-nums ${cellClass(
                        row.assignedLeads || 0
                      )}`}
                      onClick={() =>
                        row.assignedLeads
                          ? openLeadsPopup(row, { assigned: true })
                          : undefined
                      }
                    >
                      {row.assignedLeads || 0}
                    </td>
                    <td
                      className={`whitespace-nowrap px-4 py-2.5 text-right tabular-nums ${cellClass(
                        row.freshLeads || 0
                      )}`}
                      onClick={() =>
                        row.freshLeads ? openLeadsPopup(row, { fresh: true }) : undefined
                      }
                    >
                      {row.freshLeads || 0}
                    </td>
                    {connected.map((col) => (
                      <td
                        key={`d-${row.id}-${col.key}`}
                        className={`border border-[#F3F4F6] px-1 py-2.5 text-center tabular-nums ${cellClass(
                          row.buckets?.[col.key] || 0
                        )}`}
                        style={{ width: colMinWidth(col.label), minWidth: colMinWidth(col.label), maxWidth: 132 }}
                        onClick={() =>
                          row.buckets?.[col.key]
                            ? openLeadsPopup(row, { bucket: col.key })
                            : undefined
                        }
                      >
                        {row.buckets?.[col.key] || 0}
                      </td>
                    ))}
                    {notConnected.map((col) => (
                      <td
                        key={`d-${row.id}-${col.key}`}
                        className={`border border-[#F3F4F6] px-1 py-2.5 text-center tabular-nums ${cellClass(
                          row.buckets?.[col.key] || 0
                        )}`}
                        style={{ width: colMinWidth(col.label), minWidth: colMinWidth(col.label), maxWidth: 132 }}
                        onClick={() =>
                          row.buckets?.[col.key]
                            ? openLeadsPopup(row, { bucket: col.key })
                            : undefined
                        }
                      >
                        {row.buckets?.[col.key] || 0}
                      </td>
                    ))}
                    <td
                      className={`whitespace-nowrap px-4 py-2.5 text-right font-semibold tabular-nums ${cellClass(
                        metrics.totalCalls
                      )}`}
                      onClick={() =>
                        metrics.totalCalls ? openLeadsPopup(row) : undefined
                      }
                    >
                      {metrics.totalCalls}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
                      {metrics.connectPct}%
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
                      {row.recordings || 0}
                    </td>
                  </tr>
                  );
                })}
                {totals && (
                  <tr className="bg-[#111827] text-white">
                    <td
                      className="sticky left-0 z-10 whitespace-nowrap border-r border-[#111827] bg-[#111827] px-4 py-3 font-bold"
                      style={{ minWidth: 200 }}
                    >
                      Grand Total
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-bold">
                      —
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-bold tabular-nums">
                      {totals.assignedLeads || 0}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-bold tabular-nums">
                      {totals.freshLeads || 0}
                    </td>
                    {connected.map((col) => (
                      <td
                        key={`t-${col.key}`}
                        className="border border-[#111827] px-1 py-3 text-center font-bold tabular-nums"
                        style={{ width: colMinWidth(col.label), minWidth: colMinWidth(col.label), maxWidth: 132 }}
                      >
                        {totals.buckets?.[col.key] || 0}
                      </td>
                    ))}
                    {notConnected.map((col) => (
                      <td
                        key={`t-${col.key}`}
                        className="border border-[#111827] px-1 py-3 text-center font-bold tabular-nums"
                        style={{ width: colMinWidth(col.label), minWidth: colMinWidth(col.label), maxWidth: 132 }}
                      >
                        {totals.buckets?.[col.key] || 0}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-4 py-3 text-right font-bold tabular-nums">
                      {totalMetrics.totalCalls}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-bold tabular-nums">
                      {totalMetrics.connectPct}%
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-bold tabular-nums">
                      {totals.recordings || 0}
                    </td>
                  </tr>
                )}
                {!rows.length && (
                  <tr>
                    <td
                      colSpan={7 + connected.length + notConnected.length}
                      className="px-4 py-8 text-sm text-[#6B7280]"
                    >
                      No calls in this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <CallingLeadsModal
        open={popup.open}
        title={popup.title}
        loading={popup.loading}
        leads={popup.leads}
        onClose={() =>
          setPopup({ open: false, title: "", loading: false, leads: [] })
        }
      />
    </div>
  );
};

export default CallingReport;
